/**
 * SOW section-level inheritance utilities.
 *
 * Each SOW section maps to a set of SowConfig fields. Sections that are NOT
 * in a scope's `sowCustomizedSections` list inherit their values from the
 * global SOW template.
 */

import type { SowConfig } from '../types'

export type SowSectionKey =
  | 'legal'
  | 'deliveryNotes'
  | 'phases'
  | 'commercials'
  | 'raci'
  | 'gantt'
  | 'keyAssumptions'
  | 'notes'

export const ALL_SOW_SECTIONS: SowSectionKey[] = [
  'legal',
  'deliveryNotes',
  'phases',
  'commercials',
  'raci',
  'gantt',
  'keyAssumptions',
  'notes',
]

/** Maps each section key to the SowConfig field names it covers. */
export const SOW_SECTION_FIELDS: Record<SowSectionKey, (keyof SowConfig)[]> = {
  legal: ['legalText'],
  deliveryNotes: ['deliveryNotes'],
  phases: ['milestones', 'phases'],
  commercials: ['commercialsPricingModel', 'commercialsFixedFee', 'commercialsTmRate', 'commercialsTmCap'],
  raci: ['raciColumns', 'raciEntries'],
  gantt: ['ganttTasks'],
  keyAssumptions: ['keyAssumptions'],
  notes: ['notes'],
}

/** Human-readable labels for each section. */
export const SOW_SECTION_LABELS: Record<SowSectionKey, string> = {
  legal: 'Legal Text',
  deliveryNotes: 'Delivery Notes',
  phases: 'Phases & Variation',
  commercials: 'Commercials',
  raci: 'RACI Chart',
  gantt: 'Gantt Chart',
  keyAssumptions: 'Key Assumptions',
  notes: 'Additional Notes',
}

/**
 * Given a SowConfig field name, return which section it belongs to.
 */
export function getSectionForField(field: keyof SowConfig): SowSectionKey | null {
  for (const [section, fields] of Object.entries(SOW_SECTION_FIELDS)) {
    if ((fields as string[]).includes(field as string)) return section as SowSectionKey
  }
  return null
}

/**
 * Merge scope SOW data with template, applying section-level inheritance.
 *
 * For each section: if the section is in `customizedSections`, the scope's
 * values are kept. Otherwise, the template's values are used.
 */
export function mergeSowWithTemplate(
  scopeSow: SowConfig,
  templateSow: SowConfig,
  customizedSections: string[]
): SowConfig {
  const merged = { ...scopeSow }
  const customizedSet = new Set(customizedSections)

  for (const sectionKey of ALL_SOW_SECTIONS) {
    if (!customizedSet.has(sectionKey)) {
      // Inherit from template
      for (const field of SOW_SECTION_FIELDS[sectionKey]) {
        ;(merged as any)[field] = (templateSow as any)[field]
      }
    }
  }

  return merged
}

/**
 * Add a section to the customized list (idempotent).
 */
export function markSectionCustomized(sections: string[], sectionKey: SowSectionKey): string[] {
  if (sections.includes(sectionKey)) return sections
  return [...sections, sectionKey]
}

/**
 * Remove a section from the customized list (reset to template).
 */
export function unmarkSectionCustomized(sections: string[], sectionKey: SowSectionKey): string[] {
  return sections.filter((s) => s !== sectionKey)
}
